"use client";

import React, { createContext, useContext, useEffect, useMemo, useSyncExternalStore, useCallback, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import { I18N_CACHE_VERSION } from "@/lib/i18n-cache-version";
import { getLocaleFromPathname, getPathWithoutLocale, getPathWithLocale, type Locale } from "@/lib/i18n";
import { resolveTranslation, type InterpolationValues } from "@/lib/i18n-interpolate";
import { reportMissingKey } from "@/lib/i18n-logger";
import {
    emitTranslationStoreChange,
    getCacheKey,
    getTranslationStoreSnapshot,
    subscribeToTranslationStore,
    translationCache,
    translationInFlight,
} from "@/lib/translation-store";
import type {
    AboutTranslationKey,
    HeroTranslationKey,
    KnownComponent,
    ServicesTranslationKey,
    TranslationKeyByComponent,
    BookingTranslationKey,
    BookingFormTranslationKey,
    ChatTranslationKey,
    MyBookingsTranslationKey,
} from "@/types/i18n.generated";

type Lang = Locale;

export type {
    AboutTranslationKey,
    HeroTranslationKey,
    InterpolationValues,
    KnownComponent,
    ServicesTranslationKey,
    TranslationKeyByComponent,
    BookingTranslationKey,
    BookingFormTranslationKey,
    ChatTranslationKey,
    MyBookingsTranslationKey,
};

const HOME_COMPONENTS = [
    "Hero",
    "About",
    "Services",
    "TechnologiesSection",
    "IndustriesSection",
    "Partners",
    "ContactSection",
] as const;

const STATIC_ROUTE_COMPONENTS: Record<string, string[]> = {
    "/about": ["About"],
    "/articles": ["Articles"],
    "/book-call": ["Booking", "BookingForm"],
    "/careers": ["Careers"],
    "/chat": ["Chat"],
    "/contacts": ["Contacts"],
    "/dashboard": ["Dashboard", "MyBookings"],
    "/dashboard/my-bookings": ["MyBookings"],
    "/dashboard/referrals": ["Dashboard", "Referral"],
    "/partners": ["Partners"],
    "/referral": ["Referral"],
    "/services": ["Services"],
    "/technologies": ["Technologies"],
    "/work": ["Work"],
};

const SERVICE_ROUTE_COMPONENTS: Record<string, string[]> = {
    consulting: ["Consulting"],
    development: ["Development"],
    support: ["Support"],
};

const INDUSTRY_ROUTE_COMPONENTS: Record<string, string[]> = {
    agriculture: ["Agriculture"],
    education: ["Education"],
    finance: ["Finance"],
    healthcare: ["Healthcare"],
    hospitality: ["Hospitality"],
    insurance: ["Insurance"],
    logistics: ["Logistics"],
    marketing: ["Marketing"],
    retail: ["Retail"],
    sports: ["Sports"],
    telecom: ["Telecom"],
    utilities: ["Utilities"],
};

function useTranslationStoreVersion() {
    return useSyncExternalStore(
        subscribeToTranslationStore,
        getTranslationStoreSnapshot,
        getTranslationStoreSnapshot,
    );
}

function scheduleIdle(cb: () => void) {
    if (typeof window === "undefined") return;
    const ric = (window as unknown as { requestIdleCallback?: (fn: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    if (typeof ric === "function") {
        ric(cb, { timeout: 2000 });
        return;
    }
    window.setTimeout(cb, 0);
}

async function loadTranslationsForLang(lang: Lang, component: string) {
    if (typeof window === "undefined") {
        return {};
    }

    const cacheKey = getCacheKey(lang, component);
    const cached = translationCache.get(cacheKey);
    if (cached) {
        return cached;
    }

    const inflight = translationInFlight.get(cacheKey);
    if (inflight) {
        return inflight;
    }

    const versionedRequest = fetch(`/locales/${encodeURIComponent(component)}/${lang}.json?v=${encodeURIComponent(I18N_CACHE_VERSION)}`, {
        method: "GET",
        headers: { accept: "application/json" },
        cache: "force-cache",
    })
        .then(async (res) => {
            if (!res.ok) {
                const emptyTranslations = {};
                translationCache.set(cacheKey, emptyTranslations);
                emitTranslationStoreChange();
                return emptyTranslations;
            }

            const translations = (await res.json()) as Record<string, unknown>;
            translationCache.set(cacheKey, translations);
            emitTranslationStoreChange();
            return translations;
        })
        .catch(() => {
            const emptyTranslations = {};
            translationCache.set(cacheKey, emptyTranslations);
            emitTranslationStoreChange();
            return emptyTranslations;
        })
        .finally(() => {
            translationInFlight.delete(cacheKey);
        });

    translationInFlight.set(cacheKey, versionedRequest);
    emitTranslationStoreChange();
    return versionedRequest;
}

function getPrefetchComponentsForRoute(pathname: string): string[] {
    const normalizedPath = getPathWithoutLocale(pathname || "/") || "/";

    if (normalizedPath === "/") {
        return [...HOME_COMPONENTS];
    }

    const directMatch = STATIC_ROUTE_COMPONENTS[normalizedPath];
    if (directMatch) {
        return [...directMatch];
    }

    const segments = normalizedPath.split("/").filter(Boolean);
    const [topLevel, detail] = segments;

    if (topLevel === "services") {
        return detail ? [...(SERVICE_ROUTE_COMPONENTS[detail] ?? ["Services"])] : [...STATIC_ROUTE_COMPONENTS["/services"]];
    }

    if (topLevel === "industries") {
        if (!detail) {
            return ["Industries", "IndustriesSection"];
        }

        return [...(INDUSTRY_ROUTE_COMPONENTS[detail] ?? ["Industries"])];
    }

    if (topLevel === "technologies" && detail) {
        return ["Technologies"];
    }

    return [];
}

function getWarmupComponents(pathname: string): string[] {
    return Array.from(new Set(["Common", "Navbar", "Breadcrumb", ...getPrefetchComponentsForRoute(pathname)]));
}

type I18nContext = {
    lang: Lang;
    setLang: (l: Lang) => void;
    toggle: () => void;
    t: (key: string, componentOrValues?: string | InterpolationValues, values?: InterpolationValues) => string;
    isHydrated: boolean;
    loadComponentTranslations: (component: string) => Promise<void>;
    hasComponentTranslations: (component: string) => boolean;
    isStaticMessages: boolean;
};

const I18n = createContext<I18nContext | null>(null);

export function LanguageProvider({
    initialMessages,
    initialLang,
    children,
}: {
    initialMessages?: Record<string, Record<string, unknown>>;
    initialLang?: Lang;
    children: React.ReactNode;
}) {
    const router = useRouter();
    const pathname = usePathname();
    useTranslationStoreVersion();
    const lang = initialLang ?? getLocaleFromPathname(pathname || "");
    const pendingLangRef = useRef<Lang | null>(null);
    const isHydrated = true;
    const isStaticMessages = Boolean(initialMessages);

    if (initialMessages) {
        for (const [component, translations] of Object.entries(initialMessages)) {
            const cacheKey = getCacheKey(lang, component);
            if (!translationCache.has(cacheKey)) {
                translationCache.set(cacheKey, translations);
            }
        }
    }

    useEffect(() => {
        if (pendingLangRef.current === lang) {
            pendingLangRef.current = null;
        }

        const savedScroll = sessionStorage.getItem("scrollY_onLangSwitch");
        if (savedScroll !== null) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    window.scrollTo({ top: Number(savedScroll), behavior: 'instant' });
                    sessionStorage.removeItem("scrollY_onLangSwitch");
                });
            });
        }
    }, [lang, pathname]);

    const loadComponentTranslations = useCallback(async (component: string) => {
        if (isStaticMessages) {
            return;
        }

        try {
            await loadTranslationsForLang(lang, component);
        } catch (error) {
            console.error(`Failed to load translations for ${component}:`, error);
        }
    }, [isStaticMessages, lang]);

    const navigateToLang = useCallback((nextLang: Lang) => {
        const currentLang = getLocaleFromPathname(pathname || "");
        if (pendingLangRef.current === nextLang || currentLang === nextLang) {
            return;
        }

        const pathWithoutLocale = getPathWithoutLocale(pathname || "/");
        const newPath = getPathWithLocale(pathWithoutLocale, nextLang);
        const scrollY = typeof window !== "undefined" ? window.scrollY : 0;

        pendingLangRef.current = nextLang;
        sessionStorage.setItem("scrollY_onLangSwitch", String(scrollY));
        sessionStorage.setItem("prevPathOnLangSwitch", pathWithoutLocale);
        router.push(newPath, { scroll: false });
    }, [pathname, router]);

    const toggle = useCallback(() => {
        void navigateToLang(lang === "ru" ? "en" : "ru");
    }, [lang, navigateToLang]);

    const setLangWithNavigation = useCallback((next: Lang) => {
        void navigateToLang(next);
    }, [navigateToLang]);

    const t = useCallback((
        key: string,
        componentOrValues?: string | InterpolationValues,
        values?: InterpolationValues,
    ) => {
        const component = typeof componentOrValues === "string" ? componentOrValues : undefined;
        const resolvedValues =
            componentOrValues && typeof componentOrValues === "object" ? componentOrValues : values;

        if (component) {
            const cacheKey = getCacheKey(lang, component);
            const componentTranslations = translationCache.get(cacheKey);
            if (componentTranslations) {
                const result = resolveTranslation(key, resolvedValues, componentTranslations, lang);
                if (result !== null) {
                    return result;
                }
            }
        }

        const commonCacheKey = getCacheKey(lang, "Common");
        const commonTranslations = translationCache.get(commonCacheKey);
        if (commonTranslations) {
            const result = resolveTranslation(key, resolvedValues, commonTranslations, lang);
            if (result !== null) {
                return result;
            }
        }

        reportMissingKey({ key, component: component ?? "Common", lang });
        return key;
    }, [lang]);

    useEffect(() => {
        if (isStaticMessages) {
            return;
        }

        const warmupComponents = getWarmupComponents(pathname || "/");
        for (const component of warmupComponents) {
            void loadComponentTranslations(component);
        }

        scheduleIdle(() => {
            const nextLang = lang === "ru" ? "en" : "ru";
            for (const component of warmupComponents) {
                void loadTranslationsForLang(nextLang, component);
            }
        });
    }, [isStaticMessages, lang, loadComponentTranslations, pathname]);

    const value = useMemo(() => ({
        lang,
        setLang: setLangWithNavigation,
        toggle,
        t,
        isHydrated,
        loadComponentTranslations,
        hasComponentTranslations: (component: string) => translationCache.has(getCacheKey(lang, component)),
        isStaticMessages
    }), [isStaticMessages, lang, setLangWithNavigation, toggle, t, isHydrated, loadComponentTranslations]);

    return <I18n.Provider value={value}>{children}</I18n.Provider>
}

export function useI18n() {
    const ctx = useContext(I18n);
    if (!ctx) throw new Error("useI18n must be used within LanguageProvider");
    return ctx;
}

export function useComponentTranslations<C extends KnownComponent>(component: C): {
    t: (
        key: TranslationKeyByComponent[C] | string,
        fallbackOrValues?: string | InterpolationValues,
        values?: InterpolationValues,
    ) => string;
    isLoading: boolean;
    isHydrated: boolean;
    translationsLoaded: boolean;
};
export function useComponentTranslations(component: string): {
    t: (key: string, fallbackOrValues?: string | InterpolationValues, values?: InterpolationValues) => string;
    isLoading: boolean;
    isHydrated: boolean;
    translationsLoaded: boolean;
};
export function useComponentTranslations(component: string) {
    const { t, loadComponentTranslations, lang, isHydrated, hasComponentTranslations, isStaticMessages } = useI18n();
    useTranslationStoreVersion();
    const translationsLoaded = hasComponentTranslations(component);
    const isLoading = isStaticMessages ? false : translationInFlight.has(getCacheKey(lang, component));

    React.useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        if (translationsLoaded || isLoading) {
            return;
        }

        void loadComponentTranslations(component);
    }, [component, isLoading, loadComponentTranslations, translationsLoaded]);

    const translate = React.useCallback((
        key: string,
        fallbackOrValues?: string | InterpolationValues,
        values?: InterpolationValues,
    ) => {
        const fallback = typeof fallbackOrValues === "string" ? fallbackOrValues : undefined;
        const resolvedValues =
            fallbackOrValues && typeof fallbackOrValues === "object" ? fallbackOrValues : values;

        try {
            const value = t(key, component, resolvedValues);
            if (fallback && value === key) return fallback;
            return value;
        } catch (error) {
            console.error(`Translation error for key "${key}" in component "${component}":`, error);
            return fallback ?? key;
        }
    }, [t, component]);

    return { t: translate, isLoading, isHydrated, translationsLoaded };
}
